import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { SiteProvider, useSite } from './contexts/SiteContext';
import { DynamicHeader } from './components/DynamicHeader';
import { DynamicFooter } from './components/DynamicFooter';
import { WhatsAppWidget } from './components/WhatsAppWidget';
import { SitePreviewBar } from './components/SitePreviewBar';
import { SEOHead } from './components/SEOHead';
import { RedirectToMainSite } from './components/RedirectToMainSite';
import { Toaster } from './components/ui/sonner';

// Page imports
import { DynamicHome } from './pages/DynamicHome';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { Blog } from './pages/Blog';
import { Resources } from './pages/Resources';
import { ConditionPage } from './pages/ConditionPage';
import { MainHubHome } from './pages/MainHubHome';
import { TestimonialsPage } from './pages/TestimonialsPage';
import { FAQPage } from './pages/FAQPage';
import { ShoulderPainTreatmentHyderabad } from './pages/ShoulderPainTreatmentHyderabad';
import { ShoulderAnatomy } from './pages/ShoulderAnatomy';
import { ShoulderPainAtNight } from './pages/ShoulderPainAtNight';
import { NonSurgicalShoulderTreatment } from './pages/NonSurgicalShoulderTreatment';

// Shoulder condition pages (static, evidence-based content)
import { FrozenShoulder } from './pages/conditions/FrozenShoulder';
import { RotatorCuffTear } from './pages/conditions/RotatorCuffTear';
import { ShoulderDislocation } from './pages/conditions/ShoulderDislocation';
import { ShoulderArthritis } from './pages/conditions/ShoulderArthritis';
import { ShoulderImpingement } from './pages/conditions/ShoulderImpingement';
import { ShoulderReplacement } from './pages/conditions/ShoulderReplacement';
import { BicepsTendonInjury } from './pages/conditions/BicepsTendonInjury';
import { ShoulderFracture } from './pages/conditions/ShoulderFracture';
import { ShoulderPain } from './pages/conditions/ShoulderPain';
import { ArthroscopicShoulderSurgery } from './pages/conditions/ArthroscopicShoulderSurgery';
import { CalcificTendonitis } from './pages/conditions/CalcificTendonitis';
import { SLAPTears } from './pages/conditions/SLAPTears';
import { ClavicleFracture } from './pages/conditions/ClavicleFracture';
import { ScapulaFracture } from './pages/conditions/ScapulaFracture';
import { ACJointInjury } from './pages/conditions/ACJointInjury';
import { SCJointInjury } from './pages/conditions/SCJointInjury';
import { LabralTears } from './pages/conditions/LabralTears';
import { MDI } from './pages/conditions/MDI';
import { ReverseShoulderReplacement } from './pages/conditions/ReverseShoulderReplacement';

import './App.css';

// Map of condition slugs to their static components (for shoulder site)
const SHOULDER_CONDITION_PAGES = {
  '/frozen-shoulder': FrozenShoulder,
  '/rotator-cuff-tear': RotatorCuffTear,
  '/shoulder-dislocation': ShoulderDislocation,
  '/shoulder-arthritis': ShoulderArthritis,
  '/shoulder-impingement': ShoulderImpingement,
  '/shoulder-replacement': ShoulderReplacement,
  '/biceps-tendon-injury': BicepsTendonInjury,
  '/shoulder-fracture': ShoulderFracture,
  '/shoulder-pain': ShoulderPain,
  '/arthroscopic-shoulder-surgery': ArthroscopicShoulderSurgery,
  '/calcific-tendonitis': CalcificTendonitis,
  '/slap-tears': SLAPTears,
  '/clavicle-fracture': ClavicleFracture,
  '/scapula-fracture': ScapulaFracture,
  '/ac-joint-injury': ACJointInjury,
  '/sc-joint-injury': SCJointInjury,
  '/labral-tears': LabralTears,
  '/mdi': MDI,
  '/reverse-shoulder-replacement': ReverseShoulderReplacement,
};

// Inner app with routing (needs access to site context)
const AppContent = () => {
  const { isMainSite, conditions, siteId } = useSite();

  return (
    <div className="App min-h-screen flex flex-col">
      <SEOHead />
      <SitePreviewBar />
      <DynamicHeader />
      <main className="flex-grow">
        <Routes>
          {/* Home page - different for main hub vs specialty sites */}
          <Route path="/" element={isMainSite() ? <MainHubHome /> : <DynamicHome />} />
          
          {/* Pages that redirect to main site for specialty sites */}
          <Route path="/about" element={isMainSite() ? <About /> : <RedirectToMainSite path="/about" />} />
          <Route path="/testimonials" element={isMainSite() ? <TestimonialsPage /> : <RedirectToMainSite path="/testimonials" />} />
          <Route path="/contact" element={isMainSite() ? <Contact /> : <RedirectToMainSite path="/contact" />} />
          
          {/* Educational pages - available on all sites */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/resources" element={<Resources />} />
          
          {/* SEO Hub Pages - Shoulder site */}
          {siteId === 'shoulder' && (
            <>
              <Route path="/shoulder-pain-treatment-hyderabad" element={<ShoulderPainTreatmentHyderabad />} />
              <Route path="/shoulder-anatomy" element={<ShoulderAnatomy />} />
              <Route path="/shoulder-pain-at-night" element={<ShoulderPainAtNight />} />
              <Route path="/non-surgical-shoulder-treatment-hyderabad" element={<NonSurgicalShoulderTreatment />} />
            </>
          )}
          
          {/* Shoulder condition pages - use static evidence-based pages */}
          {siteId === 'shoulder' && Object.entries(SHOULDER_CONDITION_PAGES).map(([slug, Component]) => (
            <Route 
              key={slug}
              path={slug} 
              element={<Component />} 
            />
          ))}
          
          {/* Dynamic condition routes for other sites */}
          {siteId !== 'shoulder' && conditions?.map((condition) => (
            <Route 
              key={condition.id}
              path={condition.slug} 
              element={<ConditionPage conditionId={condition.id} />} 
            />
          ))}
          
          {/* Fallback for unknown routes */}
          <Route path="*" element={<DynamicHome />} />
        </Routes>
      </main>
      <DynamicFooter />
      <WhatsAppWidget />
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <SiteProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </LanguageProvider>
    </SiteProvider>
  );
}

export default App;
